import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerCheckMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { CurrentHoldTimesData } from '../current-hold-times.model';
import { CurrentHoldTimesService } from '../current-hold-times.service';

@Component({
    selector: 'cv-current-hold-times-pm',
    templateUrl: './current-hold-times-pm.component.html',
    styleUrl: './current-hold-times-pm.component.scss'
})
export class CurrentHoldTimesPmComponent {
  @Input() moduleManager: ModuleManager;
  @Input() moduleKey: string;
  @ViewChild('currentHoldTimeGrid') agGrid: AgGridAngular;
  @ViewChild("searchBox", { read: ElementRef }) searchBox: ElementRef<HTMLInputElement>;

  public data: CurrentHoldTimesData = null; // A copy of data returned by loadData will be stored here.
  public filterValue = ""; // This variable contains the contents of the search bar.
  public showFilter = false;
  // Ag-grid configuration:
  public gridOptions: GridOptions = null;
  public searchPlaceHolder = ""; // cxone search is localized in this fashion
  public searchOpen = false;
  public noData = false;
  public dataLoadSuccess = false;

  loadingPromise: Promise<ApiResponse<CurrentHoldTimesData>> = null;
  abortLoad: () => void = null;
  isAborting = false;
  params: any;

  private gridState: any = null; // This object holds previous state for ag-grid, and saves it to
  private isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
  private isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.
  private localizationStrings: Map<string, string> = new Map<string, string>(); // This is used for localizations.
  private stateSaveTimer: number;
  private gridReady = false;
  private restoreInProgress = false;

  constructor(
      private agGridHelperService: AgGridHelperService,
      private cvLocalizationService: CVLocalizationService, // Used to pull translations.  Most strings should be localized in the markup aspx or html file, but there are some strings (like AgGrid) that need to be localized in the controller code.
      private service: CurrentHoldTimesService,
      public config: CvConfigurationService
  ) {
      this.loadLocalizations();
  }

  get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; }

  ngOnInit(): void {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
      this.moduleManager.showLoader = true;

      // Module Configuration:
      this.moduleManager.setOptions({
          enableWidthClass: false,
          widgetMode: false,
          onSettingsApplied: settings => {
              this.loadData(false);
          },
          hasRefresh: true,
          refreshType: ModuleManagerRefreshType.VeryShort,
          onRefresh: isAutoRefresh => {
              if (!isAutoRefresh)
                  this.moduleManager.showLoader = true;
              return this.loadData(isAutoRefresh);
          },
          canRefresh: () => !this.isDataLoading,
      });

      this.moduleManager.addMenuItem({
          name: "Columns",
          icon: "columns",
          getEnabled: () => this.gridOptions?.columnDefs?.length > 0,
          type: "checkMenu",
          getCheckItems: () => this.gridOptions.columnDefs,
          getItemName: (i) => this.gridOptions.columnDefs[i].headerName,
          isItemChecked: (i) => {
              if ((this.gridOptions.columnDefs[i] as ColDef).hide == null) {
                  this.prepareTableState();
                  (this.gridOptions.columnDefs[i] as ColDef).hide = !this.gridState.columns[i].visible;
              }
              return !(this.gridOptions.columnDefs[i] as ColDef).hide;
          },
          onCheckClicked: (i) => this.toggleColumnVisible(i),
      } as ModuleManagerCheckMenu);

      this.moduleManager.addHeaderItem({
          name: "Search",
          icon: "search",
          click: () => this.toggleOpenSearch(),
      });

      this.moduleManager.addState({
          name: "tableState",
          getStateData: () => this.gridState,
          onStateLoaded: (stateData) => {
              if (!stateData) { // No table state loaded
                  return;
              }

              this.gridState = stateData;
              this.restoreColumnState();
          },
          autoLoad: true
      });

      this.moduleManager.registerEvents({
          onDashboardActivated() {
              if (this.agGrid?.api && this.gridReady) {
                  setTimeout(this.agGridHelperService.sizeColumns(this.gridOptions));
              }
          }
      });
  }

  toggleOpenSearch(): void {
      if (this.searchOpen) {
          this.searchOpen = false;
          this.clearFilter();
      } else {
          this.searchOpen = true;
      }
  }

  toggleColumnVisible(i) {
      // toggle column visibility
      (this.gridOptions.columnDefs[i] as ColDef).hide = !(this.gridOptions.columnDefs[i] as ColDef).hide;
      this.agGrid?.api.setColumnVisible((this.gridOptions.columnDefs[i] as ColDef).colId,
          !(this.gridOptions.columnDefs[i] as ColDef).hide);

      this.prepareTableState();
      this.queueStateSave();
  }

  applyFilter() {
      if (this.agGrid?.api) { // notify filter change
          this.agGrid.api.onFilterChanged();
      }
  }

  clearFilter() {
      this.filterValue = "";
      this.applyFilter();
  }

  /**
   * Saves the grid's state save, debounced to 500ms.
   */
  private queueStateSave() {
      // Cancel previous save attempt, since it hasn't been 500ms, yet.
      if (this.stateSaveTimer) {
          clearTimeout(this.stateSaveTimer);
          this.stateSaveTimer = null;
      }

      // Create a timer to save the state 500ms from now.
      this.stateSaveTimer = window.setTimeout(async () => {
          this.stateSaveTimer = null;

          if (!this.agGrid?.api) {
              return;
          }

          // generate state from grid
          const gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
          if (gridState != null) {
              this.gridState = gridState;
          }

          // This will save all moduleManager.addState definitions and show the publish bar on shared dashboards
          if (await this.moduleManager.saveState()) {
              this.moduleManager.requestPublishNeeded();
          }
      }, 500/*ms*/);
  }

  /**
   * Loads the localizations for this module, then sets up the grid.
   */
  private async loadLocalizations() {
      try {
          this.localizationStrings = await this.cvLocalizationService.GetSection("Module");
      } finally {
          this.setupGrid();
      }
  }

  private setupGrid() {
      this.gridOptions = this.agGridHelperService.createGridOptions({
          // default column options
          defaultColDef: {
              sortable: true,
              filter: true,
              resizable: true,
          },

          // grid options
          accentedSort: true,
          animateRows: true,
          suppressCellFocus: true,
          suppressScrollOnNewData: true,

          context: {
              moduleManager: this.moduleManager
          },

          components: {
              groupRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
          },

          // grid data
          columnDefs: [
              {
                  cellRenderer: "groupRenderer",
                  colId: "Agent",
                  field: "Group",
                  headerName: this.cvLocalizationService.getCachedText("Module", "Hold_Agent_Header", "Agent"),
                  type: "groupColumn",
              },
              {
                  colId: "Team",
                  field: "Team",
                  filter: "agTextColumnFilter",
                  headerName: this.cvLocalizationService.getCachedText("Module", "Team_Header", "Team"),
              },
              {
                  colId: "StartTime",
                  field: "StartTimeDisplay",
                  filter: "agTextColumnFilter",
                  headerName: this.cvLocalizationService.getCachedText("Module", "Hold_Start_Header", "On Hold Start"),
                  comparator: (valueA, valueB, nodeA, nodeB) =>
                      this.agGridHelperService.dateComparator(new Date(nodeA.data.StartTime), new Date(nodeB.data.StartTime)),
              },
              {
                  colId: "Project",
                  field: "Project",
                  filter: "agTextColumnFilter",
                  headerName: this.cvLocalizationService.getCachedText("Module", "Project_Header", "Project"),
              },
              {
                  colId: "HoldTime",
                  field: "HoldTime",
                  filter: "agTextColumnFilter",
                  headerName: this.cvLocalizationService.getCachedText("Module", "Hold_Mins_Header", "Hold Time"),
                  comparator: (valueA, valueB) => {
                      let totalSecondsA = 0;
                      let totalSecondsB = 0;

                      totalSecondsA += Number(valueA.substring((valueA.indexOf("mins") + 4), valueA.indexOf("secs")));
                      if (valueA.indexOf("hrs") > 0) {
                          totalSecondsA += Number(valueA.substring((valueA.indexOf("hrs") + 3), valueA.indexOf("mins"))) * 60;
                          totalSecondsA += Number(valueA.substring(0, valueA.indexOf("hrs"))) * 60 * 60;
                      } else {
                          totalSecondsA += Number(valueA.substring(0, valueA.indexOf("mins"))) * 60;
                      }

                      totalSecondsB += Number(valueB.substring((valueB.indexOf("mins") + 4), valueB.indexOf("secs")));
                      if (valueB.indexOf("hrs") > 0) {
                          totalSecondsB += Number(valueB.substring((valueB.indexOf("hrs") + 3), valueB.indexOf("mins"))) * 60;
                          totalSecondsB += Number(valueB.substring(0, valueB.indexOf("hrs"))) * 60 * 60;
                      } else {
                          totalSecondsB += Number(valueB.substring(0, valueB.indexOf("mins"))) * 60;
                      }

                      if (totalSecondsA === totalSecondsB) return 0;
                      return (totalSecondsA > totalSecondsB) ? 1 : -1;
                  },
              },
          ],
          rowData: [],

          // external filtering (our search bar)
          isExternalFilterPresent: () => !!this.filterValue,
          doesExternalFilterPass: (node) => this.agGridHelperService.doesExternalFilterPass(node, this.filterValue),

          // grid events
          onFirstDataRendered: (e) => this.sizeColumns(),
          onGridReady: (e) => {
              this.gridReady = true;
              if (this.gridState) {
                  this.restoreColumnState();
              }
          },
          getRowId: ({data: rowData}) => rowData.Group.groupID,
          onGridSizeChanged: (e) => {
              if (e.clientWidth > 0 && this.isResizeWaiting) {
                  this.agGridHelperService.sizeColumnsSmart(e);
              }
              this.applyFilter();
          },
          onSortChanged: (e) => this.queueStateSave(),
          onColumnMoved: (e) => this.queueStateSave(),
          onColumnPinned: (e) => this.queueStateSave(),
          onColumnResized: event => {
              if (event.source === "uiColumnResized"
                || (event.source === "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                  this.queueStateSave();
              }
          },
          onColumnVisible: (e) => {
              if (e.source === "api") return; // handle API event separately

              // This event is called both on a single column toggle and when initializing Ag-Grid. Only map the hide to isVisible in a single column case.
              if (e.columns?.length === 1 && e.columns[0].getColDef().hide !== !e.columns[0].isVisible()) {
                  const columnDefinition: ColDef = this.gridOptions.columnDefs.find((c: ColDef) => c.field === e.columns[0].getColId());
                  columnDefinition.hide = !e.columns[0].isVisible();
              }
              this.agGridHelperService.sizeColumnsSmart(e);
              this.queueStateSave();
          },
          onCellClicked: (params) => {
              this.params = params;
          }
      }, {
          enableCopy: true
      });
  }

  private sizeColumns() {
      if (!this.data || !this.agGrid?.api) {
          return;
      }

      // helper function to size columns
      this.agGridHelperService.sizeColumns(this.agGrid?.api, this.gridOptions);

      // load state data
      this.restoreColumnState();
  }

  private restoreColumnState() {
      if (!this.gridState.columns || !this.agGrid?.api) {
          return;
      }

      // keep state from being saved while restoring
      this.restoreInProgress = true;

      this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);

      // states can be saved again
      this.restoreInProgress = false;
  }

  private prepareTableState() {
      if (!this.agGrid?.api)
          return;

      // generate state from grid
      const gridState = this.agGridHelperService.getGridState(this.agGrid.api);
      if (gridState != null) {
          this.gridState = gridState;
      }
  }

  private async loadData(isAutoRefresh) {
      this.isDataLoading = true;
      const response = await this.service.loadCurrentHoldTimes(this.moduleKey, isAutoRefresh, this.isFirstLoad);
      this.isDataLoading = false;
      this.data = response.data;

      if (this.agGrid?.api) {
          this.agGrid.api.setGridOption('rowData', this.data.RowValues);
      } else {
          this.gridOptions.rowData = this.data.RowValues;
      }

      // Update column headers:
      if (this.data.ColumnHeaders && typeof this.data.ColumnHeaders === "object") {
          for (const colDef of (this.gridOptions.columnDefs) as ColDef[]) {
              const colId = colDef.colId || colDef.field;
              if (typeof this.data.ColumnHeaders[colId] === "string") {
                  switch(colId)
                  {
                      case "Agent":
                          colDef.headerName = this.cvLocalizationService.getCachedText("Module", "Hold_Agent_Header", "Agent");
                          break;
                      case "Team":
                          colDef.headerName = this.cvLocalizationService.getCachedText("Module", "Team_Header", "Team");
                          break;
                      case "StartTime":
                          colDef.headerName = this.cvLocalizationService.getCachedText("Module", "Hold_Start_Header", "On Hold Start");
                          break;
                      case "Project":
                          colDef.headerName = this.cvLocalizationService.getCachedText("Module", "Project_Header", "Project");
                          break;
                      case "HoldTime":
                          colDef.headerName = this.cvLocalizationService.getCachedText("Module", "Hold_Mins_Header", "Hold Time");
                          break;
                  }
              }
          }

          if (this.agGrid?.api) {
              this.agGrid.api.refreshHeader();
          }
      }

      this.dataLoadSuccess = true;
      this.moduleManager.showLoader = false;
      this.moduleManager.setAutoRefreshReady();

      this.isFirstLoad = false;
  }
}
